import { Link } from "@tanstack/react-router";
import { MessagesSquare } from "lucide-react";
import { useRef, useState } from "react";

import { ROTA_DE_CONVERSAS } from "@/components/app-nav";
import { idsDoChatRapido, ListaDoChatRapido, useAbrirConversa } from "@/components/conversas/ChatRapido";
import { useChatRapido } from "@/components/conversas/ChatRapidoProvider";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useIsMobile } from "@/hooks/use-mobile";
import { contagem } from "@/lib/plural";

/**
 * O ícone do topo (ao lado do sino). TUT-590 — volta a ABRIR as conversas por
 * cima da tela atual (decisão do dono em 02/11, C1, revertendo a TUT-338a, que
 * em 28/09 o fizera levar a `/conversas`): no desktop, a lista num popover, e
 * a conversa escolhida vira uma janela no canto (`ChatRapido`); no celular, a
 * folha de tela cheia. A caixa completa segue em "Ver todas".
 *
 * O selo soma as não lidas da caixa única inteira (`GET /conversas`, a mesma
 * consulta da tela, renovada a cada 60 s pelo `ChatRapidoProvider`).
 *
 * TUT-545 — mora fora do `ChatPanel`: é do escritório e lê a rota do menu
 * (`app-nav.ts`), e o `ChatPanel` é o corpo da seção Mensagens do portal, que
 * não alcança o menu do escritório (`portal-sem-linha.test.ts`).
 */
export function ChatPanelButton() {
  const chat = useChatRapido();
  const celular = useIsMobile();
  const abrirConversa = useAbrirConversa();
  const [aberto, setAberto] = useState(false);
  // A janela recém-aberta recebe o foco quando o popover fecha (e não o ícone).
  const focarDepois = useRef<string | null>(null);
  const totalNaoLidas = chat?.totalNaoLidas ?? 0;

  return (
    <Popover
      open={aberto && !celular}
      onOpenChange={(abrir) => {
        if (abrir && celular) chat?.setFolhaAberta(true);
        else setAberto(abrir);
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="discreto"
          className="relative size-[var(--control-h)] p-0"
          aria-label={
            totalNaoLidas > 0 ? `Conversas: ${contagem(totalNaoLidas, "não lida", "não lidas")}` : "Conversas"
          }
        >
          <MessagesSquare aria-hidden="true" />
          {totalNaoLidas > 0 ? (
            <span className="absolute -right-[0.15rem] -top-[0.15rem] h-[1.05rem] min-w-[1.05rem] rounded-full bg-orange px-[0.25rem] text-center text-legenda font-bold leading-[1.05rem] text-white">
              {totalNaoLidas > 9 ? "9+" : totalNaoLidas}
            </span>
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        aria-labelledby="chat-rapido-lista"
        className="flex max-h-[min(34rem,calc(100dvh-6rem))] w-[22rem] max-w-[calc(100vw-2rem)] flex-col overflow-hidden p-0"
        onCloseAutoFocus={(evento) => {
          const id = focarDepois.current;
          focarDepois.current = null;
          if (!id) return;
          evento.preventDefault();
          requestAnimationFrame(() => document.getElementById(idsDoChatRapido(id).campo)?.focus());
        }}
      >
        <ListaDoChatRapido
          idDoTitulo="chat-rapido-lista"
          acoes={
            <Button asChild variant="discreto" size="sm">
              <Link to={ROTA_DE_CONVERSAS} onClick={() => setAberto(false)}>
                Ver todas
              </Link>
            </Button>
          }
          onAbrir={(conversa) => {
            if (abrirConversa(conversa)) focarDepois.current = conversa.id;
            setAberto(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
